import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus } from 'lucide-react';
import { createMeeting } from '@/api/meetings';

export function NewMeetingDialog() {
  const [title, setTitle] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { subscription, checkSubscriptionStatus } = useAuth();
  
  // Event-based subscription check when dialog opens
  useEffect(() => {
    if (isOpen) {
      // Check subscription status when dialog opens
      checkSubscriptionStatus(true).catch(error => {
        console.error("Failed to refresh subscription status:", error);
      });
    }
  }, [isOpen, checkSubscriptionStatus]);
  
  // Check if user can create new meetings
  const canCreateMeetings = !subscription || 
    subscription.status === 'trial' || 
    subscription.status === 'paid';
    
  // If on free tier, user cannot create new meetings
  const isFreeUser = subscription?.status === 'free';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Check subscription status again before creating meeting
      const currentSubscription = await checkSubscriptionStatus(true);
      
      // Verify user can create meetings
      if (currentSubscription.status === 'free') {
        setIsOpen(false);
        navigate("/subscription-choice");
        return;
      }
      
      // Call the API to create a new meeting
      const response = await createMeeting(title);
      setIsOpen(false);
      navigate(`/app/meetings/${response.meeting._id}`);
    } catch (error) {
      console.error('Error creating meeting:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <div className="w-full"> {/* Added wrapper div */}
        <DialogTrigger asChild disabled={!canCreateMeetings}>
          <Button 
            className="w-full md:w-auto bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/40" 
            disabled={!canCreateMeetings}
            title={isFreeUser ? "Upgrade to Premium to create new meetings" : ""}
          > 
            <Plus className="mr-2 h-4 w-4" />
            New Meeting
          </Button>
        </DialogTrigger>
      </div>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Meeting</DialogTitle>
        </DialogHeader>
        {isFreeUser ? (
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 dark:bg-amber-950 p-4 rounded-md border border-amber-200 dark:border-amber-800">
              <h3 className="font-medium text-amber-800 dark:text-amber-300 mb-2">Free Tier Limitation</h3>
              <p className="text-amber-700 dark:text-amber-400 text-sm">
                Your free trial has expired. On the free tier, you can view and edit existing meetings, 
                but cannot create new ones.
              </p>
              <div className="mt-4">
                <Button 
                  className="w-full" 
                  onClick={() => {
                    setIsOpen(false);
                    navigate("/subscription-choice");
                  }}
                >
                  Upgrade to Premium
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Meeting Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter meeting title..."
                required
              />
            </div>
            <Button type="submit" className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/40" disabled={isLoading}>
              {isLoading ? 'Creating...' : 'Create Meeting'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
